import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { TechAssistCallout } from '../../components/page/TechAssistCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
const materials = [{
  title: '6061-T6 Aluminum',
  href: '/applications/aluminum/',
  inserts: 'APGX 331R / 332R MG12 · -30R chipbreakers',
  summary: 'Roughing with MG12 inserts at .025–.050 IPR, or -30R chipbreakers for lighter feeds at .010–.020 IPR.'
}, {
  title: 'Copper & Soft Alloys',
  href: '/applications/copper-soft-alloys/',
  inserts: 'Peck cycle for chip control',
  summary: 'A peck cycle for copper and soft aluminum alloys, with starting values published for oxygen-free copper.'
}, {
  title: 'Titanium',
  href: '/applications/titanium/',
  inserts: 'APGX 331R / 332R DR80',
  summary: 'Honed, MAX-coated DR80 inserts with a 100–200 SFM, .003–.007 IPR suggested starting range.'
}];
export function ApplicationsOverview() {
  usePageMeta({
    title: 'Applications: Aluminum, Copper & Titanium | Robertson Precision',
    description: 'Choose Shear Geometry inserts and published starting guidance for 6061-T6 aluminum, copper and soft aluminum alloys, and titanium. Technical assistance: 650-224-7511.',
    canonical: canonicalFor('/applications/')
  });
  return <>
      <PageHeader eyebrow="Applications" title="Applications" lead="Published starting guidance covers three material groups. Choose the material, review the insert and starting values, then talk the setup through with the technical staff." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Applications'
    }]} data-id="element-700" data-mp="23sd2tfkk4d5o" />

      <ContentSection id="materials" title="Choose the material" data-id="element-701" data-mp="2iqdrboj3vmps">
        <ul className="grid gap-px border border-rule bg-rule lg:grid-cols-3" data-id="element-702" data-mp="1t31os9qirs2l">
          {materials.map(m => <li key={m.href} className="flex bg-white" data-id="element-703" data-mp="1o48boif5a32y">
              <Link to={m.href} className="group flex w-full flex-col p-6 transition-colors duration-150 ease-precise hover:bg-warm lg:p-7" data-id="element-704" data-mp="22rjm2gzlbh1h">
                <span className="font-display text-[22px] font-medium text-navy group-hover:text-copper" data-id="element-705" data-mp="2bshoiuudrghy">
                  {m.title}
                </span>
                <span className="mt-3 inline-flex self-start border border-rule bg-warm px-2.5 py-1 font-mono text-[10.5px] tracking-[0.06em] text-graphite" data-id="element-706" data-mp="26svbalejy095">
                  {m.inserts}
                </span>
                <span className="mt-4 text-[14.5px] leading-relaxed text-graphite/85" data-id="element-707" data-mp="2gscdutyiby0w">
                  {m.summary}
                </span>
                <span className="mt-auto inline-flex items-center gap-2 pt-6 text-[14px] font-semibold text-navy" data-id="element-708" data-mp="mdtgt6wqimq9">
                  View guidance
                  <ArrowRightIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-709" data-mp="2y1f15uumonxd" />
                </span>
              </Link>
            </li>)}
        </ul>
        <p className="rp-measure mt-6 text-[14px] leading-relaxed text-graphite/75" data-id="element-710" data-mp="2obvgqkr9om0e">
          Values are published starting guidance. Machine horsepower,
          workholding, part rigidity, tool overhang and machine limits govern
          the actual parameters for your setup.
        </p>
      </ContentSection>

      <ContentSection id="help" tone="warm" data-id="element-711" data-mp="2jcs9qurq85ps">
        <TechAssistCallout location="applications-overview" showPrepList data-id="element-712" data-mp="25c7s41xjsfxv" />
      </ContentSection>

      <RelatedLinks links={[{
      label: 'Starting Data',
      href: '/technical-resources/starting-data/',
      description: 'All published values in one table.'
    }, {
      label: 'Workholding & Safety',
      href: '/technical-resources/safety/',
      description: 'Setup checks and the workholding chart.'
    }, {
      label: 'Inserts & Chipbreakers',
      href: '/products/inserts/',
      description: 'MG12 and DR80 inserts.'
    }, {
      label: 'Tool Selection',
      href: '/technical-resources/tool-selection/',
      description: 'Holder, insert and application.'
    }]} data-id="element-713" data-mp="2abaybpp9ks7f" />
    </>;
}